import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { Input } from "./input";
import { Label } from "./label";

export interface ConfirmRequest {
  title: string;
  description?: ReactNode;
  /** Label of the button that goes ahead; the caller translates it. */
  confirmLabel: string;
  cancelLabel: string;
  /** Red button for anything that destroys something. */
  destructive?: boolean;
}

/** A question that also wants a sentence back — the replacement for `window.prompt` (US-23.6). */
export interface PromptRequest extends ConfirmRequest {
  input: {
    label: string;
    placeholder?: string;
    /** The confirm button stays disabled until something is typed. */
    required?: boolean;
  };
}

/**
 * "Are you sure?", as a dialog rather than as `window.confirm` (EP-23 decision 13).
 *
 * <p>`window.confirm` is a browser chrome box: it cannot be translated, cannot be styled, cannot be
 * given a sentence explaining what is about to happen, and in some browsers it can be suppressed
 * entirely — so a destructive action would then go ahead unasked.
 *
 * <p>The hook returns a promise so that replacing a call is mechanical:
 * {@code if (!(await confirm({...}))) return;}
 */
interface ConfirmApi {
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  /** Resolves with what was typed, or `null` when the person backed out. */
  promptFor: (request: PromptRequest) => Promise<string | null>;
}

const ConfirmContext = createContext<ConfirmApi | null>(null);

function hasInput(request: ConfirmRequest | PromptRequest | null): request is PromptRequest {
  return request !== null && "input" in request;
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | PromptRequest | null>(null);
  const [text, setText] = useState("");
  const settle = useRef<((answer: string | null) => void) | null>(null);

  const answer = useCallback((value: string | null) => {
    settle.current?.(value);
    settle.current = null;
    setRequest(null);
    setText("");
  }, []);

  const ask = useCallback((next: ConfirmRequest | PromptRequest) => {
    // A second question while one is open answers the first with "no": two overlapping dialogs would
    // leave a promise nobody ever settles.
    settle.current?.(null);
    setRequest(next);
    setText("");
    return new Promise<string | null>((resolve) => {
      settle.current = resolve;
    });
  }, []);

  const value = useMemo<ConfirmApi>(
    () => ({
      confirm: (next) => ask(next).then((given) => given !== null),
      promptFor: (next) => ask(next),
    }),
    [ask],
  );

  const missingRequired = hasInput(request) && Boolean(request.input.required) && text.trim() === "";

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog open={request !== null} onOpenChange={(open) => (open ? undefined : answer(null))}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle>{request?.title}</DialogTitle>
            {request?.description ? <DialogDescription>{request.description}</DialogDescription> : null}
          </DialogHeader>
          {hasInput(request) ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="confirm-dialog-input">{request.input.label}</Label>
              <Input
                id="confirm-dialog-input"
                data-testid="confirm-dialog-input"
                autoFocus
                placeholder={request.input.placeholder}
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => answer(null)} data-testid="confirm-dialog-cancel">
              {request?.cancelLabel}
            </Button>
            <Button
              variant={request?.destructive ? "destructive" : "default"}
              disabled={missingRequired}
              // An empty string is a real answer for a confirmation: "yes, and nothing typed".
              onClick={() => answer(text)}
              data-testid="confirm-dialog-confirm"
            >
              {request?.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

function useConfirmApi() {
  const api = useContext(ConfirmContext);
  if (!api) {
    throw new Error("useConfirm() needs a <ConfirmDialogProvider> above it");
  }
  return api;
}

/**
 * @throws Error when no {@link ConfirmDialogProvider} is mounted — a silent `false` would let a
 *         missing provider look like a user who said no
 */
export function useConfirm() {
  return useConfirmApi().confirm;
}

/** The same dialog with a field in it, for the places that used to call `window.prompt` (US-23.6). */
export function usePrompt() {
  return useConfirmApi().promptFor;
}
